import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  inventoryApi,
  type AddLotPayload,
  type UpdateLotPayload,
  type CreateFieldDefPayload,
  type CreateMaterialPayload,
  type CreateProfilePayload,
  type ImportDecision,
  type ImportRequest,
  type UpdateFieldDefPayload,
  type UpdateMaterialPayload,
  type UpdateProfilePayload,
} from '@/api/inventoryApi'
import type { InventoryProfileType, MaterialStockFilter, ReorderFilter } from '@/types/inventory'
import { useTenant } from '@/providers/TenantProvider'
import { statusOf } from '@/lib/httpError'

/** Params accepted by `GET /inventory/materials`. */
type MaterialListParams = {
  type?: string
  status?: string
  /** Low / out of stock / expired, as the re-order rule judges it (z8nrz7f7dz). */
  stock?: MaterialStockFilter
  q?: string
  page?: number
  pageSize?: number
}

// ─── Queries ────────────────────────────────────────────────────────────────

/** Paginated/filtered material list for the selected tenant. */
export function useMaterials(params: MaterialListParams = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'materials', tenantId, params],
    enabled: !!tenantId,
    queryFn: () => inventoryApi.listMaterials(tenantId as string, params),
  })
}

/**
 * Who is holding a lot's reserved quantity. Fetched only when a lot row is
 * expanded and actually has something held, so the list view costs nothing.
 */
export function useLotHolds(lotId: string | null, enabled: boolean) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'lot-holds', tenantId, lotId],
    enabled: !!tenantId && !!lotId && enabled,
    queryFn: () => inventoryApi.listLotHolds(tenantId as string, lotId as string),
    staleTime: 15_000,
  })
}

/** Backend hard cap on `pageSize` for `GET /inventory/materials`. */
const MATERIALS_MAX_PAGE_SIZE = 200

/**
 * Every material for the selected tenant. The list endpoint is capped at
 * {@link MATERIALS_MAX_PAGE_SIZE} rows per page, so this pages through it and
 * merges the results — callers that need the whole catalog (pickers, lookups)
 * must use this instead of a single `useMaterials` call.
 */
export function useAllMaterials(
  params: Omit<MaterialListParams, 'page' | 'pageSize'> = {},
  /** `enabled: false` defers the fetch — a picker loads the catalogue when it opens, not with its page. */
  options: { enabled?: boolean } = {},
) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'materials', 'all', tenantId, params],
    enabled: !!tenantId && (options.enabled ?? true),
    queryFn: () => listAllMaterials(tenantId as string, params),
  })
}

/** Every page of the materials list, merged in page order. */
async function listAllMaterials(tenantId: string, params: Omit<MaterialListParams, 'page' | 'pageSize'>) {
  const first = await inventoryApi.listMaterials(tenantId, {
    ...params,
    page: 1,
    pageSize: MATERIALS_MAX_PAGE_SIZE,
  })
  const items = [...first.items]
  const pages = Math.ceil(first.total / MATERIALS_MAX_PAGE_SIZE)
  // The rest in parallel, merged in page order — the same list as reading
  // them one after another, without waiting on each in turn (z8nrz7e8kw).
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, pages - 1) }, (_, i) =>
      inventoryApi.listMaterials(tenantId, {
        ...params,
        page: i + 2,
        pageSize: MATERIALS_MAX_PAGE_SIZE,
      }),
    ),
  )
  for (const next of rest) {
    // Defensive: a short/empty page means there is nothing more to merge.
    if (next.items.length === 0) break
    items.push(...next.items)
  }
  return { items, total: first.total }
}

/**
 * Only these materials, in the list's own shape (z8nrz7e8kw): what a process
 * step needs to show the lots of the few materials it is configured with,
 * instead of the whole catalogue page by page. Keyed under
 * `['inventory', 'materials']`, so every stock write that refreshes the
 * catalogue refreshes this too.
 */
export function useMaterialsByIds(ids: readonly string[]) {
  const { selectedTenantId: tenantId } = useTenant()
  const wanted = [...new Set(ids.filter(Boolean))].sort()
  return useQuery({
    queryKey: ['inventory', 'materials', 'by-id', tenantId, wanted],
    enabled: !!tenantId && wanted.length > 0,
    queryFn: async () => {
      const chunks: string[][] = []
      for (let i = 0; i < wanted.length; i += MATERIALS_MAX_PAGE_SIZE) chunks.push(wanted.slice(i, i + MATERIALS_MAX_PAGE_SIZE))
      try {
        const pages = await Promise.all(
          chunks.map((chunk) =>
            inventoryApi.listMaterials(tenantId as string, { ids: chunk, pageSize: MATERIALS_MAX_PAGE_SIZE }),
          ),
        )
        const items = pages.flatMap((p) => p.items)
        return { items, total: items.length }
      } catch (e) {
        // A backend without the `ids` filter refuses it (400, unknown
        // property). Fall back to the catalogue, as before, so the step still
        // shows its lots whichever of the two is deployed first.
        if (statusOf(e) !== 400) throw e
        const all = await listAllMaterials(tenantId as string, {})
        const items = all.items.filter((m) => wanted.includes(m.id))
        return { items, total: items.length }
      }
    },
  })
}

/**
 * Materials against their reorder point (z8nrz7f7dz), for the Re-Order tab.
 * Keyed under `['inventory', 'materials']`, like `useMaterialsByIds`, so every
 * stock or material write that refreshes the catalogue refreshes this too.
 */
export function useReorderList(filter: ReorderFilter, type?: string, q?: string, options: { enabled?: boolean } = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'materials', 'reorder', tenantId, { filter, type, q }],
    enabled: !!tenantId && (options.enabled ?? true),
    queryFn: () => inventoryApi.listReorder(tenantId as string, { filter, type, q }),
    placeholderData: keepPreviousData,
  })
}

/** The Re-Order counts alone — the tab label's. Keyed for the same invalidation as the list. */
export function useReorderSummary() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'materials', 'reorder-summary', tenantId],
    enabled: !!tenantId,
    queryFn: () => inventoryApi.getReorderSummary(tenantId as string),
  })
}

/** Inventory-type procedures still holding material short of release (I13's Pending Inventory tab). */
export function usePendingInventory() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'pending', tenantId],
    enabled: !!tenantId,
    queryFn: () => inventoryApi.listPending(tenantId as string),
  })
}

/** A single material by id. Pass `null` to skip the query (e.g. while a form is closed). */
export function useMaterial(id: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'material', tenantId, id],
    enabled: !!tenantId && !!id,
    queryFn: () => inventoryApi.getMaterial(tenantId as string, id as string),
  })
}

/** Profiles for the selected tenant, optionally narrowed to one profile type. */
export function useProfiles(profileType?: InventoryProfileType) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'profiles', tenantId, profileType],
    enabled: !!tenantId,
    queryFn: () => inventoryApi.listProfiles(tenantId as string, profileType),
  })
}

/** Field definitions for the selected tenant, optionally narrowed to one profile type. */
export function useFieldDefs(profileType?: InventoryProfileType) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'fieldDefs', tenantId, profileType],
    enabled: !!tenantId,
    queryFn: () => inventoryApi.listFieldDefs(tenantId as string, profileType),
  })
}

/**
 * The Material columns, field definitions and named profiles an import may
 * target.
 *
 * Only fetched once the wizard is open: it is a full read of the tenant's
 * catalogue, and nothing else on the Master Data screen needs it.
 */
export function useImportSchema(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'importSchema', tenantId],
    enabled: enabled && !!tenantId,
    queryFn: () => inventoryApi.getImportSchema(tenantId as string),
  })
}

// ─── Mutations ──────────────────────────────────────────────────────────────

type SaveMaterialInput =
  | { id?: undefined; payload: CreateMaterialPayload }
  | { id: string; payload: UpdateMaterialPayload }

/** Create (no `id`) or update (`id` present) a material. */
export function useSaveMaterial() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveMaterialInput) => {
      if (!tenantId) throw new Error('No tenant selected')
      if (input.id !== undefined) {
        return inventoryApi.updateMaterial(tenantId, input.id, input.payload)
      }
      return inventoryApi.createMaterial(tenantId, input.payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] })
      queryClient.invalidateQueries({ queryKey: ['inventory', 'material'] })
    },
  })
}

/** Connect a material to an Inventory procedure template (replaces any existing connection). */
export function useConnectMaterialProcedure() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (v: { materialId: string; procedureTemplateId: string }) => {
      if (!tenantId) throw new Error('No tenant selected')
      return inventoryApi.connectProcedure(tenantId, v.materialId, v.procedureTemplateId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] })
      queryClient.invalidateQueries({ queryKey: ['inventory', 'material'] })
    },
  })
}

/** Disconnect a material from its Inventory procedure. */
export function useDisconnectMaterialProcedure() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (materialId: string) => {
      if (!tenantId) throw new Error('No tenant selected')
      return inventoryApi.disconnectProcedure(tenantId, materialId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] })
      queryClient.invalidateQueries({ queryKey: ['inventory', 'material'] })
    },
  })
}

/** Delete a material by id. */
export function useDeleteMaterial() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => {
      if (!tenantId) throw new Error('No tenant selected')
      return inventoryApi.deleteMaterial(tenantId, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] })
      queryClient.invalidateQueries({ queryKey: ['inventory', 'material'] })
    },
  })
}

type SaveProfileInput =
  | { id?: undefined; payload: CreateProfilePayload }
  | { id: string; payload: UpdateProfilePayload }

/** Create (no `id`) or update (`id` present) a profile. */
export function useSaveProfile() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveProfileInput) => {
      if (!tenantId) throw new Error('No tenant selected')
      if (input.id !== undefined) {
        return inventoryApi.updateProfile(tenantId, input.id, input.payload)
      }
      return inventoryApi.createProfile(tenantId, input.payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'profiles'] })
      // An inventory_unit preset carries re-order parameters, so the verdicts
      // on the material rows and the Re-Order list may have moved.
      queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] })
    },
  })
}

/** Add a lot (lot#, quantity, expiry) to a material. */
export function useAddLot() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (v: { materialId: string; payload: AddLotPayload }) => {
      if (!tenantId) throw new Error('No tenant selected')
      return inventoryApi.addLot(tenantId, v.materialId, v.payload)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] })
      // A received lot is a task on Inventory → Tasks (z8nrz7fjzc).
      void queryClient.invalidateQueries({ queryKey: ['maintenance-events'] })
    },
  })
}

/** Edit a lot in place — chiefly to give it a storage zone and bin. */
export function useUpdateLot() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (v: { materialId: string; lotId: string; payload: UpdateLotPayload }) => {
      if (!tenantId) throw new Error('No tenant selected')
      return inventoryApi.updateLot(tenantId, v.materialId, v.lotId, v.payload)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] }),
  })
}

/** Remove a lot from a material — irreversible, so it is a signed act (see `inventoryApi.removeLot`). */
export function useRemoveLot() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (v: {
      materialId: string
      lotId: string
      signingToken: string
      signatureMeaning: string
      reason?: string
    }) => {
      if (!tenantId) throw new Error('No tenant selected')
      return inventoryApi.removeLot(tenantId, v.materialId, v.lotId, {
        signingToken: v.signingToken,
        signatureMeaning: v.signatureMeaning,
        reason: v.reason,
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] }),
  })
}

/** Delete a field definition by id. */
export function useDeleteFieldDef() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => {
      if (!tenantId) throw new Error('No tenant selected')
      return inventoryApi.deleteFieldDef(tenantId, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'fieldDefs'] })
    },
  })
}

/** Delete a profile (shared preset) by id. */
export function useDeleteProfile() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => {
      if (!tenantId) throw new Error('No tenant selected')
      return inventoryApi.deleteProfile(tenantId, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'profiles'] })
    },
  })
}

type SaveFieldDefInput =
  | { id?: undefined; payload: CreateFieldDefPayload }
  | { id: string; payload: UpdateFieldDefPayload }

/** Create (no `id`) or update (`id` present) a field definition. */
export function useSaveFieldDef() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveFieldDefInput) => {
      if (!tenantId) throw new Error('No tenant selected')
      if (input.id !== undefined) {
        return inventoryApi.updateFieldDef(tenantId, input.id, input.payload)
      }
      return inventoryApi.createFieldDef(tenantId, input.payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'fieldDefs'] })
    },
  })
}

/**
 * Dry-run a file. Writes nothing, so it invalidates nothing.
 *
 * A mutation rather than a query: the same file may legitimately be previewed
 * again after the caller changes a column mapping, and a cached result keyed on
 * the file would return the answer to the previous mapping.
 */
export function usePreviewImport() {
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation({
    mutationFn: (payload: ImportRequest) => {
      if (!tenantId) throw new Error('No tenant selected')
      return inventoryApi.previewImport(tenantId, payload)
    },
  })
}

/** Apply a previewed import. All rows or none. */
export function useCommitImport() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ImportRequest & { decisions: ImportDecision[] }) => {
      if (!tenantId) throw new Error('No tenant selected')
      return inventoryApi.commitImport(tenantId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] })
      queryClient.invalidateQueries({ queryKey: ['inventory', 'material'] })
      // A commit can mint named profiles, so the profile lists behind every
      // category picker are stale too.
      queryClient.invalidateQueries({ queryKey: ['inventory', 'profiles'] })
      queryClient.invalidateQueries({ queryKey: ['inventory', 'importSchema'] })
    },
  })
}
